import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cx } from "./cx";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ className, invalid, ...rest }, ref) {
  return <input ref={ref} aria-invalid={invalid || undefined} className={cx("ui-input", className)} {...rest} />;
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, ...rest },
  ref,
) {
  return <textarea ref={ref} aria-invalid={invalid || undefined} className={cx("ui-input", className)} {...rest} />;
});

export interface NativeSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  /** Classes for the wrapper that draws the chevron. */
  wrapperClassName?: string;
}

/**
 * A styled native <select>. Prefer it for short option lists: it is accessible
 * for free and on a phone it opens the OS picker, which beats any custom popup.
 */
export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(function NativeSelect(
  { className, wrapperClassName, invalid, children, ...rest },
  ref,
) {
  return (
    <span className={cx("ui-select", wrapperClassName)}>
      <select ref={ref} aria-invalid={invalid || undefined} className={cx("ui-input", className)} {...rest}>
        {children}
      </select>
    </span>
  );
});

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  /**
   * A string, or a React Hook Form error object. RHF types nested field errors
   * as objects; accepting both keeps every call site from coercing.
   */
  error?: string | { message?: string } | undefined;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

/**
 * A labelled field. The label always sits above the control and is never a
 * placeholder pretending to be one. The error replaces the hint in the same
 * slot, so the form does not jump when validation fires.
 */
export function Field({ label, hint, error, required, htmlFor, children, className }: FieldProps) {
  const message = typeof error === "string" ? error : error?.message;
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="ui-label">
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="ui-required">
              *
            </span>
            <span className="ui-sr-only"> (required)</span>
          </>
        )}
      </label>
      {children}
      {message ? (
        <p role="alert" className="ui-error">
          {message}
        </p>
      ) : (
        hint && <p className="ui-hint">{hint}</p>
      )}
    </div>
  );
}
